import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  ReportFlowPages,
} from '../../../components/ui/Report';
import {
  carryForwardSummary,
  isOpeningBondedLine,
  isOpeningCashEntry,
} from '../closeChecklist';
import type { MgaBondedLine, MgaCashEntry, MgaCrewRequest, MgaPeriod } from '../useData';
import { ReportHeader } from './ReportHeader';
import { cashEntryTypeLabel, formatReportMoney, reportNum, reportRound2 } from './reportMeta';

const WAGE_CHUNK = 12;
const CASH_CHUNK = 14;
const BONDED_CHUNK = 16;
const REQUEST_CHUNK = 16;
const PREV_BAL_CHUNK = 18;

function chunkArray<T>(items: T[], size: number): T[][] {
  if (items.length === 0) return [[]];
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

function cashBal(cash: MgaCashEntry[]) {
  return cash.reduce((s, e) => {
    const amt = reportNum(e.amountPeriod ?? e.amount);
    return e.entryType === 'EXPENSE' ? s - amt : s + amt;
  }, 0);
}

function bondedBal(bonded: MgaBondedLine[]) {
  const inn = bonded
    .filter((l) => l.section === 'STOCK' || l.section === 'INCOMING')
    .reduce((s, r) => s + reportNum(r.amount), 0);
  const out = bonded
    .filter((l) => l.section === 'EXPENSE')
    .reduce((s, r) => s + reportNum(r.amount), 0);
  return inn - out;
}

function wageScaleTotal(line: {
  contractTotalWage?: number | null;
  wageValues?: Array<{
    columnType: string;
    includeInTotal: boolean;
    periodAmount: number;
  }>;
}): number {
  const fromContract = reportNum(line.contractTotalWage);
  if (Math.abs(fromContract) > 0.0001) return fromContract;
  const values = line.wageValues ?? [];
  return reportRound2(
    values
      .filter(
        (v) =>
          v.columnType !== 'DEDUCTION' &&
          v.columnType !== 'INFO' &&
          v.includeInTotal,
      )
      .reduce((s, v) => s + reportNum(v.periodAmount), 0),
  );
}

function requestTypeLabel(t: string) {
  if (t === 'ALLOTMENT') return 'Allotment';
  if (t === 'CASH_BY_MASTER') return 'Cash by master';
  if (t === 'SHEET_COLUMN') return 'Sheet column';
  return t;
}

export type ClosePackReportProps = {
  period: MgaPeriod;
  cash: MgaCashEntry[];
  bonded: MgaBondedLine[];
  requests: MgaCrewRequest[];
  companyLogoSrc?: string | null;
  companyLogoAlt?: string;
};

export const ClosePackReport: React.FC<ClosePackReportProps> = ({
  period,
  cash,
  bonded,
  requests,
  companyLogoSrc = null,
  companyLogoAlt,
}) => {
  const ccy = period.currency || 'USD';
  const lines = period.lines ?? [];

  const broughtForward = useMemo(
    () =>
      carryForwardSummary({
        previousBalance: period.totals?.previousBalance,
        cash,
        bonded,
      }),
    [period.totals?.previousBalance, cash, bonded],
  );

  const prevBalanceLines = useMemo(
    () => lines.filter((l) => Math.abs(reportNum(l.previousBalance)) > 0.005),
    [lines],
  );

  const openingCashRows = useMemo(() => cash.filter(isOpeningCashEntry), [cash]);

  const openingBondedRows = useMemo(
    () =>
      bonded.filter(
        (l) =>
          (l.section === 'STOCK' || l.section === 'INCOMING') && isOpeningBondedLine(l),
      ),
    [bonded],
  );

  const cashBookRows = useMemo(() => {
    const sorted = [...cash].sort((a, b) => {
      const da = a.entryDate ? String(a.entryDate).slice(0, 10) : '';
      const db = b.entryDate ? String(b.entryDate).slice(0, 10) : '';
      if (da !== db) return da.localeCompare(db);
      return a.id - b.id;
    });
    let running = 0;
    return sorted.map((e) => {
      const amt = reportNum(e.amountPeriod ?? e.amount);
      const signed = e.entryType === 'EXPENSE' ? -amt : amt;
      running = reportRound2(running + signed);
      return { e, periodAmt: amt, signed, balance: running };
    });
  }, [cash]);

  const cashClosing = cashBookRows.length ? cashBookRows[cashBookRows.length - 1].balance : 0;

  const bondedIn = useMemo(
    () =>
      [...bonded]
        .filter((l) => l.section === 'STOCK' || l.section === 'INCOMING')
        .sort((a, b) => {
          const da = a.entryDate ? String(a.entryDate).slice(0, 10) : '';
          const db = b.entryDate ? String(b.entryDate).slice(0, 10) : '';
          if (da !== db) return da.localeCompare(db);
          return a.id - b.id;
        }),
    [bonded],
  );

  const bondedOut = useMemo(
    () =>
      [...bonded]
        .filter((l) => l.section === 'EXPENSE')
        .sort((a, b) => {
          const da = a.entryDate ? String(a.entryDate).slice(0, 10) : '';
          const db = b.entryDate ? String(b.entryDate).slice(0, 10) : '';
          if (da !== db) return da.localeCompare(db);
          return a.id - b.id;
        }),
    [bonded],
  );

  const bondedInTotal = useMemo(
    () => reportRound2(bondedIn.reduce((s, l) => s + reportNum(l.amount), 0)),
    [bondedIn],
  );
  const bondedOutTotal = useMemo(
    () => reportRound2(bondedOut.reduce((s, l) => s + reportNum(l.amount), 0)),
    [bondedOut],
  );

  const requestRows = useMemo(() => {
    return [...requests].sort((a, b) => {
      const ta = a.requestType.localeCompare(b.requestType);
      if (ta !== 0) return ta;
      return (a.crewLine?.crewName || '').localeCompare(b.crewLine?.crewName || '');
    });
  }, [requests]);

  const bookTotals = useMemo(() => {
    const cashByMaster = requests
      .filter((r) => r.requestType === 'CASH_BY_MASTER')
      .reduce((s, r) => s + reportNum(r.amount), 0);
    const allotReqs = requests
      .filter((r) => r.requestType === 'ALLOTMENT')
      .reduce((s, r) => s + reportNum(r.amount), 0);
    return {
      cashOnHand: reportRound2(cashBal(cash)),
      bondedBalance: reportRound2(bondedBal(bonded)),
      cashByMaster: reportRound2(cashByMaster),
      allotReqs: reportRound2(allotReqs),
    };
  }, [cash, bonded, requests]);

  const wageTotals = useMemo(() => {
    let wage = 0;
    let ot = 0;
    let extra = 0;
    let prev = 0;
    let ded = 0;
    let net = 0;
    for (const l of lines) {
      wage += wageScaleTotal(l);
      ot += reportNum(l.otTotal ?? (Number(l.overtimeHours) || 0) * (Number(l.overtimeRate) || 0));
      extra += reportNum(l.extraEarnings);
      prev += reportNum(l.previousBalance);
      ded += reportNum(l.totalDeductions ?? l.deductions);
      net += reportNum(l.netDue);
    }
    return {
      wage: reportRound2(wage),
      ot: reportRound2(ot),
      extra: reportRound2(extra),
      prev: reportRound2(prev),
      ded: reportRound2(ded),
      net: reportRound2(net),
    };
  }, [lines]);

  const firstHeader = useMemo(
    () => (
      <ReportHeader
        period={period}
        title="Master's close pack"
        eyebrow="Master's general account — full period"
        companyLogoSrc={companyLogoSrc}
        companyLogoAlt={companyLogoAlt}
      />
    ),
    [companyLogoAlt, companyLogoSrc, period],
  );

  const lastFooter = useMemo(
    () => (
      <section className="mga-report-section">
        <h2>Approvals / signatures</h2>
        <table className="mga-report-table mga-report-sign-table">
          <thead>
            <tr>
              <th>Role</th>
              <th>Name</th>
              <th>Date</th>
              <th>Signature</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Master</td>
              <td>{period.masterApprovedByName || ''}</td>
              <td>
                {period.masterApprovedAt ? formatDateGlobal(period.masterApprovedAt) : ''}
              </td>
              <td className="mga-report-sign-cell" />
            </tr>
            <tr>
              <td>Office / Superintendent</td>
              <td />
              <td />
              <td className="mga-report-sign-cell" />
            </tr>
            <tr>
              <td>Accounts</td>
              <td />
              <td />
              <td className="mga-report-sign-cell" />
            </tr>
          </tbody>
        </table>
      </section>
    ),
    [period.masterApprovedAt, period.masterApprovedByName],
  );

  const { items, itemKeys } = useMemo(() => {
    const blocks: React.ReactNode[] = [];
    const keys: Array<string | number> = [];

    const push = (key: string, node: React.ReactNode) => {
      keys.push(key);
      blocks.push(node);
    };

    push(
      'bf-summary',
      <section className="mga-report-section">
        <h2>1. Brought forward (previous month)</h2>
        <table className="mga-report-table">
          <thead>
            <tr>
              <th>Item</th>
              <th className="mga-report-num">Amount ({ccy})</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Previous crew balances (sum)</td>
              <td className="mga-report-num">
                {formatReportMoney(broughtForward.previousBalance)}
              </td>
            </tr>
            <tr>
              <td>Opening cash</td>
              <td className="mga-report-num">{formatReportMoney(broughtForward.openingCash)}</td>
            </tr>
            <tr>
              <td>Opening bonded stock</td>
              <td className="mga-report-num">{formatReportMoney(broughtForward.openingBonded)}</td>
            </tr>
          </tbody>
        </table>
      </section>,
    );

    chunkArray(prevBalanceLines, PREV_BAL_CHUNK).forEach((chunk, ci) => {
      const start = ci * PREV_BAL_CHUNK;
      push(
        `prev-bal-${ci}`,
        <section className="mga-report-section">
          <h3 className="mga-report-subhead">
            Crew previous balances{ci > 0 ? ' (continued)' : ''}
          </h3>
          <table className="mga-report-table">
            <thead>
              <tr>
                <th className="mga-report-num">A/A</th>
                <th>Crew</th>
                <th>Rank</th>
                <th className="mga-report-num">Previous balance</th>
              </tr>
            </thead>
            <tbody>
              {chunk.length === 0 ? (
                <tr>
                  <td colSpan={4} className="mga-report-muted">
                    No previous balances carried forward
                  </td>
                </tr>
              ) : (
                chunk.map((l, i) => (
                  <tr key={l.id}>
                    <td className="mga-report-num">{start + i + 1}</td>
                    <td>{l.crewName}</td>
                    <td>{l.rankName || '—'}</td>
                    <td className="mga-report-num">{formatReportMoney(l.previousBalance)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>,
      );
    });

    push(
      'opening-cash-bonded',
      <section className="mga-report-section">
        <h3 className="mga-report-subhead">Opening cash entries</h3>
        <table className="mga-report-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th className="mga-report-num">Period amt</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {openingCashRows.length === 0 ? (
              <tr>
                <td colSpan={4} className="mga-report-muted">
                  No opening cash from prior period
                </td>
              </tr>
            ) : (
              openingCashRows.map((e) => (
                <tr key={e.id}>
                  <td>{e.entryDate ? formatDateGlobal(e.entryDate) : '—'}</td>
                  <td>{e.description}</td>
                  <td className="mga-report-num">
                    {formatReportMoney(e.amountPeriod ?? e.amount)}
                  </td>
                  <td>{e.remarks || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <h3 className="mga-report-subhead">Opening bonded stock</h3>
        <table className="mga-report-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th className="mga-report-num">Amount</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {openingBondedRows.length === 0 ? (
              <tr>
                <td colSpan={4} className="mga-report-muted">
                  No opening bonded stock from prior period
                </td>
              </tr>
            ) : (
              openingBondedRows.map((l) => (
                <tr key={l.id}>
                  <td>{l.entryDate ? formatDateGlobal(l.entryDate) : '—'}</td>
                  <td>{l.description}</td>
                  <td className="mga-report-num">{formatReportMoney(l.amount)}</td>
                  <td>{l.remarks || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>,
    );

    const wageChunks = chunkArray(lines, WAGE_CHUNK);
    wageChunks.forEach((chunk, ci) => {
      const start = ci * WAGE_CHUNK;
      const isLastWage = ci === wageChunks.length - 1;
      push(
        `wages-${ci}`,
        <section className="mga-report-section">
          <h2>
            2. Wages / payroll{ci > 0 ? ' (continued)' : ''}
          </h2>
          <table className="mga-report-table">
            <thead>
              <tr>
                <th className="mga-report-num">A/A</th>
                <th>Crew</th>
                <th>Rank</th>
                <th className="mga-report-num">Days</th>
                <th className="mga-report-num">Wage</th>
                <th className="mga-report-num">OT</th>
                <th className="mga-report-num">Extra</th>
                <th className="mga-report-num">Prev. bal.</th>
                <th className="mga-report-num">Deductions</th>
                <th className="mga-report-num">Net due</th>
              </tr>
            </thead>
            <tbody>
              {chunk.length === 0 ? (
                <tr>
                  <td colSpan={10} className="mga-report-muted">
                    No crew lines
                  </td>
                </tr>
              ) : (
                chunk.map((l, i) => {
                  const ot =
                    l.otTotal ??
                    (Number(l.overtimeHours) || 0) * (Number(l.overtimeRate) || 0);
                  return (
                    <tr key={l.id}>
                      <td className="mga-report-num">{start + i + 1}</td>
                      <td>{l.crewName}</td>
                      <td>{l.rankName || '—'}</td>
                      <td className="mga-report-num">{formatReportMoney(l.daysInPeriod)}</td>
                      <td className="mga-report-num">{formatReportMoney(wageScaleTotal(l))}</td>
                      <td className="mga-report-num">{formatReportMoney(ot)}</td>
                      <td className="mga-report-num">{formatReportMoney(l.extraEarnings)}</td>
                      <td className="mga-report-num">{formatReportMoney(l.previousBalance)}</td>
                      <td className="mga-report-num">
                        {formatReportMoney(l.totalDeductions ?? l.deductions)}
                      </td>
                      <td className="mga-report-num">{formatReportMoney(l.netDue)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {isLastWage && lines.length > 0 ? (
              <tfoot>
                <tr>
                  <td colSpan={4} className="mga-report-emph">
                    Totals ({ccy})
                  </td>
                  <td className="mga-report-num mga-report-emph">
                    {formatReportMoney(wageTotals.wage)}
                  </td>
                  <td className="mga-report-num mga-report-emph">
                    {formatReportMoney(wageTotals.ot)}
                  </td>
                  <td className="mga-report-num mga-report-emph">
                    {formatReportMoney(wageTotals.extra)}
                  </td>
                  <td className="mga-report-num mga-report-emph">
                    {formatReportMoney(wageTotals.prev)}
                  </td>
                  <td className="mga-report-num mga-report-emph">
                    {formatReportMoney(wageTotals.ded)}
                  </td>
                  <td className="mga-report-num mga-report-emph">
                    {formatReportMoney(wageTotals.net)}
                  </td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </section>,
      );
    });

    push(
      'payroll-totals',
      <section className="mga-report-section">
        <h3 className="mga-report-subhead">Period payroll totals</h3>
        <table className="mga-report-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th className="mga-report-num">Amount ({ccy})</th>
            </tr>
          </thead>
          <tbody>
            {(
              [
                ['Gross payable', period.totals?.grossPayable],
                ['OT total', period.totals?.otTotal],
                ['Extra earnings', period.totals?.extraEarnings],
                ['Previous balance', period.totals?.previousBalance],
                ['Total deductions', period.totals?.totalDeductions ?? period.totals?.deductions],
                ['Allotments', period.totals?.allotmentAmount],
                ['Net due to crew', period.totals?.netDue],
              ] as const
            ).map(([label, value]) => (
              <tr key={label}>
                <td>{label}</td>
                <td className="mga-report-num">{formatReportMoney(value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>,
    );

    push(
      'books-totals',
      <section className="mga-report-section">
        <h2>3. MGA books — period totals</h2>
        <table className="mga-report-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th className="mga-report-num">Amount ({ccy})</th>
            </tr>
          </thead>
          <tbody>
            {(
              [
                ['Gross payable', period.totals?.grossPayable],
                ['Total deductions', period.totals?.totalDeductions ?? period.totals?.deductions],
                ['Net due to crew', period.totals?.netDue],
                ['Cash on hand', bookTotals.cashOnHand],
                ['Bonded store balance', bookTotals.bondedBalance],
                ['Cash by master (Books)', bookTotals.cashByMaster],
                ['Allotments (Books)', bookTotals.allotReqs],
              ] as const
            ).map(([label, value]) => (
              <tr key={label}>
                <td>{label}</td>
                <td className="mga-report-num">{formatReportMoney(value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>,
    );

    const cashChunks = chunkArray(cashBookRows, CASH_CHUNK);
    cashChunks.forEach((chunk, ci) => {
      const isLastCash = ci === cashChunks.length - 1;
      push(
        `cash-${ci}`,
        <section className="mga-report-section">
          <h2>Cash book (full){ci > 0 ? ' (continued)' : ''}</h2>
          <table className="mga-report-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Description</th>
                <th>Crew</th>
                <th>CCY</th>
                <th className="mga-report-num">Amount</th>
                <th className="mga-report-num">Period amt</th>
                <th className="mga-report-num">Signed</th>
                <th className="mga-report-num">Balance</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {chunk.length === 0 ? (
                <tr>
                  <td colSpan={10} className="mga-report-muted">
                    No cash entries
                  </td>
                </tr>
              ) : (
                chunk.map(({ e, periodAmt, signed, balance }) => (
                  <tr key={e.id}>
                    <td>{e.entryDate ? formatDateGlobal(e.entryDate) : '—'}</td>
                    <td>{cashEntryTypeLabel(e.entryType)}</td>
                    <td>{e.description}</td>
                    <td>{e.crewLine?.crewName || '—'}</td>
                    <td>{e.currency || ccy}</td>
                    <td className="mga-report-num">{formatReportMoney(e.amount)}</td>
                    <td className="mga-report-num">{formatReportMoney(periodAmt)}</td>
                    <td className="mga-report-num">{formatReportMoney(signed)}</td>
                    <td className="mga-report-num">{formatReportMoney(balance)}</td>
                    <td>{e.remarks || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
            {isLastCash && cashBookRows.length > 0 ? (
              <tfoot>
                <tr>
                  <td colSpan={8} className="mga-report-emph">
                    Closing balance ({ccy})
                  </td>
                  <td className="mga-report-num mga-report-emph">
                    {formatReportMoney(cashClosing)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            ) : null}
          </table>
        </section>,
      );
    });

    const bondedInChunks = chunkArray(bondedIn, BONDED_CHUNK);
    bondedInChunks.forEach((chunk, ci) => {
      const isLastIn = ci === bondedInChunks.length - 1;
      push(
        `bonded-in-${ci}`,
        <section className="mga-report-section">
          <h2>
            Bonded store — stock / incoming{ci > 0 ? ' (continued)' : ''}
          </h2>
          <table className="mga-report-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Section</th>
                <th>Description</th>
                <th className="mga-report-num">Amount</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {chunk.length === 0 ? (
                <tr>
                  <td colSpan={5} className="mga-report-muted">
                    No stock / incoming lines
                  </td>
                </tr>
              ) : (
                chunk.map((l) => (
                  <tr key={l.id}>
                    <td>{l.entryDate ? formatDateGlobal(l.entryDate) : '—'}</td>
                    <td>{l.section}</td>
                    <td>{l.description}</td>
                    <td className="mga-report-num">{formatReportMoney(l.amount)}</td>
                    <td>{l.remarks || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
            {isLastIn && bondedIn.length > 0 ? (
              <tfoot>
                <tr>
                  <td colSpan={3} className="mga-report-emph">
                    Subtotal
                  </td>
                  <td className="mga-report-num mga-report-emph">
                    {formatReportMoney(bondedInTotal)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            ) : null}
          </table>
        </section>,
      );
    });

    const bondedOutChunks = chunkArray(bondedOut, BONDED_CHUNK);
    bondedOutChunks.forEach((chunk, ci) => {
      const isLastOut = ci === bondedOutChunks.length - 1;
      push(
        `bonded-out-${ci}`,
        <section className="mga-report-section">
          <h3 className="mga-report-subhead">
            Bonded store — stock out{ci > 0 ? ' (continued)' : ''}
          </h3>
          <table className="mga-report-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Crew</th>
                <th className="mga-report-num">Amount</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {chunk.length === 0 ? (
                <tr>
                  <td colSpan={5} className="mga-report-muted">
                    No stock out lines
                  </td>
                </tr>
              ) : (
                chunk.map((l) => (
                  <tr key={l.id}>
                    <td>{l.entryDate ? formatDateGlobal(l.entryDate) : '—'}</td>
                    <td>{l.description}</td>
                    <td>{l.crewLine?.crewName || '—'}</td>
                    <td className="mga-report-num">{formatReportMoney(l.amount)}</td>
                    <td>{l.remarks || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
            {isLastOut && bondedOut.length > 0 ? (
              <tfoot>
                <tr>
                  <td colSpan={3} className="mga-report-emph">
                    Subtotal · balance {formatReportMoney(bookTotals.bondedBalance)}
                  </td>
                  <td className="mga-report-num mga-report-emph">
                    {formatReportMoney(bondedOutTotal)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            ) : null}
          </table>
        </section>,
      );
    });

    chunkArray(requestRows, REQUEST_CHUNK).forEach((chunk, ci) => {
      push(
        `requests-${ci}`,
        <section className="mga-report-section">
          <h2>
            Allotments / cash by master (Books){ci > 0 ? ' (continued)' : ''}
          </h2>
          <table className="mga-report-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Crew</th>
                <th>Rank</th>
                <th>Allottee / detail</th>
                <th className="mga-report-num">Amount</th>
                <th>Currency</th>
              </tr>
            </thead>
            <tbody>
              {chunk.length === 0 ? (
                <tr>
                  <td colSpan={6} className="mga-report-muted">
                    No allotment or cash-by-master requests
                  </td>
                </tr>
              ) : (
                chunk.map((r) => (
                  <tr key={r.id}>
                    <td>{requestTypeLabel(r.requestType)}</td>
                    <td>{r.crewLine?.crewName || '—'}</td>
                    <td>{r.crewLine?.rankName || '—'}</td>
                    <td>
                      {[r.columnCode, r.remarks].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td className="mga-report-num">{formatReportMoney(r.amount)}</td>
                    <td>{ccy}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>,
      );
    });

    return { items: blocks, itemKeys: keys };
  }, [
    bondedIn,
    bondedInTotal,
    bondedOut,
    bondedOutTotal,
    bookTotals.bondedBalance,
    bookTotals.allotReqs,
    bookTotals.cashByMaster,
    bookTotals.cashOnHand,
    broughtForward,
    cashBookRows,
    cashClosing,
    ccy,
    lines,
    openingBondedRows,
    openingCashRows,
    period.totals,
    prevBalanceLines,
    requestRows,
    wageTotals,
  ]);

  return (
    <ReportFlowPages
      className="mga-close-pack-report"
      size="portrait"
      firstHeader={firstHeader}
      lastFooter={lastFooter}
      items={items}
      itemKeys={itemKeys}
    />
  );
};

export default ClosePackReport;
